import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getLocaleDefinition, useLocalization } from "./index";
import type { LocaleCode } from "./types";
import { isPublicPage, localizedPublicPath, publicCanonicalPath, PUBLIC_SITE_URL, readUrlLocale } from "./publicRoutes";
import organizerContent from "../content/organizerContent.json";
import publicPageContent from "../content/publicPageContent.json";

// Query-based language URLs preserve the existing routes and mounted form state.
const PublicPageHead = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { locale, setLocale, supportedLocales, t } = useLocalization();
  const previousLocation = useRef<string | null>(null);
  const pendingUrlLocale = useRef<LocaleCode | null>(null);

  useLayoutEffect(() => {
    const identity = location.pathname + location.search;
    if (previousLocation.current !== identity) {
      previousLocation.current = identity;
      const requestedLocale = readUrlLocale(location.search);
      pendingUrlLocale.current = requestedLocale;
      if (requestedLocale && requestedLocale !== locale) {
        setLocale(requestedLocale);
        return;
      }
    }
    if (pendingUrlLocale.current && pendingUrlLocale.current !== locale) return;
    pendingUrlLocale.current = null;
    if (!isPublicPage(location.pathname) && !readUrlLocale(location.search)) return;
    const currentPath = identity + location.hash;
    const nextPath = localizedPublicPath(currentPath, locale);
    if (nextPath !== currentPath) navigate(nextPath, { replace: true, state: location.state });
  }, [locale, location, navigate, setLocale]);

  useEffect(() => {
    // Replace server tags as the SPA navigates; never leave a previous page's
    // canonical or language alternatives attached to a different route.
    document.head.querySelectorAll('link[rel="canonical"], link[rel="alternate"][hreflang]')
      .forEach((node) => node.remove());
    if (!isPublicPage(location.pathname)) return;
    const path = publicCanonicalPath(location.pathname);
    const url = (language: LocaleCode) => PUBLIC_SITE_URL + localizedPublicPath(path, language);
    const canonical = document.createElement("link");
    canonical.rel = "canonical";
    canonical.href = url(locale);
    document.head.appendChild(canonical);
    [...supportedLocales.map(({ code }) => ({ language: code, href: url(code) })),
      { language: "x-default", href: url("en") }].forEach(({ language, href }) => {
      const link = document.createElement("link");
      link.rel = "alternate";
      link.hreflang = language;
      link.href = href;
      document.head.appendChild(link);
    });
    const updateMeta = (attribute: "name" | "property", name: string, value: string) => {
      let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.content = value;
    };
    updateMeta("property", "og:url", url(locale));
    updateMeta("property", "og:locale", getLocaleDefinition(locale).intlLocale.replace("-", "_"));
    const metadata = path.startsWith("/organize") ? organizerContent.metadata
      : path.startsWith("/discover") ? publicPageContent.discover
        : path === "/seny-i-rauxa" ? {
          title: "Seny i Rauxa | The spirit behind RAUXA",
          description: "RAUXA brings seny and rauxa to racing: thoughtful race design, real-world challenges, and the spark to show up and compete.",
        }
          : path === "/careers" ? {
            title: "Careers | RAUXA",
            description: "There are currently no open positions at RAUXA. Send your résumé to hello@rauxa.app for future opportunities.",
          }
            : null;
    if (metadata) {
      document.title = t(metadata.title);
      updateMeta("name", "description", t(metadata.description));
      updateMeta("property", "og:title", t(metadata.title));
      updateMeta("property", "og:description", t(metadata.description));
      updateMeta("name", "twitter:title", t(metadata.title));
      updateMeta("name", "twitter:description", t(metadata.description));
    }
  }, [locale, location.pathname, location.search, supportedLocales, t]);

  return null;
};

export default PublicPageHead;
